import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useForm, useWatch } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';
import {
  useAllProgramListQuery,
  useCreateTaskMutation,
  useCustomTaskMetadataQuery,
  useOwnProfileQuery,
  usePatientAdditionalInformationQuery,
  usePatientCareTeamQuery,
  usePatientsShortQuery,
  useTeamsShortQuery,
  useUserListShortQuery,
} from '@common/cache';
import { addTaskSchema } from '../schemas';
import { InferType } from 'yup';
import dayjs from 'dayjs';
import { formatApiError } from '@common/errors/error-formatting.ts';
import { t } from 'i18next';
import { paths, TASK_RECURRENCE } from '@common/types';
import { isEqual, toNumber } from 'lodash';
import { useDrawerStore } from '../../../store/drawer.store.ts';
import useUserStore from '../../../store/user.store.ts';

type IFormShape = InferType<typeof addTaskSchema>;

const initialValues: Partial<IFormShape> = {
  category: 'Care management',
  patient_id: '',
  title: '',
  description: '',
  due_date: '',
  assign_to: 'team',
  user_id: '',
  team_id: '',
  recurrence: '-1',
  urgency: '',
  expected_minutes: 0,
};

const reasonForExternalReferralOptions = [
  'Other',
  'Patient choice',
  'Community choice',
];

const noteTypeOptions = [
  'Email',
  'Phone call',
  'In person',
  'Reflection',
  'Text',
  'Other',
].map(value => ({
  value,
  label: value,
}));

export const useAddTask = (searchQuery: string, openOnTaskCreate: boolean) => {
  const { setDrawerOpen, setTaskId } = useDrawerStore(state => state);

  const { user } = useUserStore();

  const formMethods = useForm<IFormShape>({
    mode: 'onChange',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver<IFormShape, any, any>(addTaskSchema),
    defaultValues: {
      ...initialValues,
    },
  });

  const {
    handleSubmit,
    formState: { isDirty, isValid, isSubmitting },
    reset,
    getValues,
    setValue,
    trigger,
    control,
  } = formMethods;

  const [teamsSearchQuery, setTeamsSearchQuery] = useState('');
  const [userSearchQuery, setUserSearchQuery] = useState('');

  const { mutateAsync, isPending } = useCreateTaskMutation();
  const { data: userProfile } = useOwnProfileQuery();
  const { data: teamData = [] } = useTeamsShortQuery({
    search: teamsSearchQuery,
  });
  const { data: userData = [] } = useUserListShortQuery({
    search: userSearchQuery,
  });
  const { data: options = [] } = usePatientsShortQuery({
    search: searchQuery,
  });
  const { data: metadata } = useCustomTaskMetadataQuery();

  const patient_id = useWatch({ control, name: 'patient_id' });

  const { data: careTeamData } = usePatientCareTeamQuery(patient_id || '');
  const { data: patientAdditionalInformation } =
    usePatientAdditionalInformationQuery({ patient_id: patient_id });

  const assignOptions = [
    {
      value: 'team',
      label: t('worklist.addCustomTask.teamLabel'),
    },
    {
      value: 'user',
      label: t('worklist.addCustomTask.userLabel'),
    },
    {
      value: 'self',
      label: t('worklist.addCustomTask.selfLabel'),
    },
    {
      value: 'assignedCm',
      label: t('worklist.addCustomTask.assignedCm'),
      disabled: !careTeamData?.care_manager.id,
    },
  ];

  const urgencyOptions = ['Stat (24h)', 'Urgent (48h)', 'Routine (72h)'];

  const labRequirementsOptions = ['Fasting', 'Non-fasting'];

  const homeHealthAgencyNameOptions = [
    'Home Sweet Home',
    'Remedy',
    'Access',
    'AccentCare',
    'Advantage',
    'Apex',
    'Aspire',
    'Bella Terra',
    'Best Home Health',
    'Care manager',
    'Care partners',
    'EmpowerMe',
    'First choice',
    'Mission',
    'Salus',
    'Vitas',
    'Other',
  ];

  const homeHealthProviderStatusOptions = ['Certified', 'Needs certification'];

  const hospiceReferralOriginOptions = [
    "Sent by provider, at the Community's request",
    "Sent by provider, at the patient's / patient's family's request",
    'Made directly to care management by community',
  ];

  const externalHospiceReferralReasonOptions = [
    'Community choice',
    'Patient choice',
    'The patient lives outside our hospice agency area',
    'Other',
  ];

  const recurrenceOptions = Object.entries(TASK_RECURRENCE)
    .filter(([key]) => isNaN(Number(key)))
    .map(([key, value]) => ({
      value: value.toString(),
      label: t(`worklist.recurrence.${key.toLowerCase()}`),
    }));

  const teamOptions = teamData.map(team => ({
    id: team.id,
    name: team.name,
  }));

  const userOptions =
    userData.map(user => ({
      id: user.id,
      name: user.name,
    })) || [];

  const { data: programOptions = [] } = useAllProgramListQuery();

  const [isOpen, setIsOpen] = useState(false);
  const [isSaveChangesModalOpen, setIsSaveChangesModalOpen] = useState(false);

  const handleClose = () => {
    if (isDirty) {
      setIsSaveChangesModalOpen(true);
    } else {
      setIsOpen(false);
      reset();
      setTeamsSearchQuery('');
    }
  };

  const handleOnClick = () => {
    setIsOpen(true);
  };

  const submitHandler = async (values: IFormShape) => {
    const todayDayjs = dayjs(new Date());
    const dueDays = dayjs(values.due_date).diff(todayDayjs, 'day', true);

    try {
      const response = await mutateAsync({
        ...(values.assign_to === 'self' && { assign_to_self: true }),
        ...(values.assign_to === 'team' && { team_id: values.team_id }),
        ...(values.assign_to === 'user' && { user_id: values.user_id }),
        ...(values.assign_to === 'assignedCm' && {
          assign_to_assigned_cm: true,
        }),
        patient_id: values.patient_id,
        title: values.title,
        description: values.description,
        category: values.category,
        ...(values.due_date && { due_days: Math.max(0, Math.ceil(dueDays)) }),
        ...(values.category !== 'Pharmacy' && {
          recurrence:
            values.recurrence as paths['/task/create-custom-task']['post']['requestBody']['content']['application/json']['recurrence'],
        }),
        ...(values.category === 'Care management' && {
          provider_id: values.provider_id,
          urgency:
            values.urgency as paths['/task/create-custom-task']['post']['requestBody']['content']['application/json']['urgency'],
          order_type:
            values.order_type as paths['/task/create-custom-task']['post']['requestBody']['content']['application/json']['order_type'],
          order_set:
            values.order_set as paths['/task/create-custom-task']['post']['requestBody']['content']['application/json']['order_set'],
          ...(values.lab_requirements &&
            values.order_type === 'Labs' && {
              lab_requirements:
                values.lab_requirements as paths['/task/create-custom-task']['post']['requestBody']['content']['application/json']['lab_requirements'],
            }),
          ...(values.order_type === 'Home Health' && {
            home_health_agency_name:
              values.home_health_agency_name as paths['/task/create-custom-task']['post']['requestBody']['content']['application/json']['home_health_agency_name'],
            home_health_provider_status:
              values.home_health_provider_status as paths['/task/create-custom-task']['post']['requestBody']['content']['application/json']['home_health_provider_status'],
          }),
          ...(values.order_type === 'Hospice' && {
            hospice_referral_origin:
              values.hospice_referral_origin as paths['/task/create-custom-task']['post']['requestBody']['content']['application/json']['hospice_referral_origin'],
          }),
          ...(values.order_type === 'Hospice' &&
            values.order_set === 'External / Other' && {
              external_hospice_agency_name:
                values.external_hospice_agency_name as paths['/task/create-custom-task']['post']['requestBody']['content']['application/json']['external_hospice_agency_name'],
            }),

          ...(values.order_set === 'External Referral' && {
            external_agency_name:
              values.external_agency_name as paths['/task/create-custom-task']['post']['requestBody']['content']['application/json']['external_agency_name'],
            reason_for_external_referral:
              values.reason_for_external_referral as paths['/task/create-custom-task']['post']['requestBody']['content']['application/json']['reason_for_external_referral'],
          }),

          ...(values.order_type === 'Completed task' && {
            expected_minutes: (toNumber(values.expected_minutes) ||
              0) as paths['/task/create-custom-task']['post']['requestBody']['content']['application/json']['expected_minutes'],
            note: values.note as paths['/task/create-custom-task']['post']['requestBody']['content']['application/json']['note'],
            note_type:
              values.note_type as paths['/task/create-custom-task']['post']['requestBody']['content']['application/json']['note_type'],
            notable_call:
              values.notable_call as paths['/task/create-custom-task']['post']['requestBody']['content']['application/json']['notable_call'],
            program:
              values.note_program_id as paths['/task/create-custom-task']['post']['requestBody']['content']['application/json']['program'],
          }),

          ...(values.order_type === 'Hospice' &&
            values.order_set === 'External / Other' && {
              external_hospice_referral_reason:
                values.external_hospice_referral_reason as paths['/task/create-custom-task']['post']['requestBody']['content']['application/json']['external_hospice_referral_reason'],
            }),
          ...((values.order_set === 'Other' ||
            values.home_health_agency_name === 'Other' ||
            values.external_hospice_agency_name === 'Other' ||
            values.external_hospice_referral_reason === 'Other' ||
            values.hospice_referral_origin === 'Other' ||
            values.reason_for_external_referral === 'Other') && {
            other_specifier:
              values.other_specifier as paths['/task/create-custom-task']['post']['requestBody']['content']['application/json']['other_specifier'],
          }),
        }),
      });

      if (response.data.id && openOnTaskCreate) {
        setTaskId(response.data.id);
        setDrawerOpen(true);
      }

      setIsSaveChangesModalOpen(false);
      setIsOpen(false);
      reset();
      setTeamsSearchQuery('');
      toast.success('You created a new task');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const onSubmit = handleSubmit(submitHandler);

  const handleCloseSaveChangesModal = () => {
    setIsSaveChangesModalOpen(false);
  };

  const handleDiscardChanges = () => {
    setIsSaveChangesModalOpen(false);
    setIsOpen(false);
    reset();
    setTeamsSearchQuery('');
  };

  const handleSaveChanges = async () => {
    await onSubmit();
  };

  const taskType = getValues('category');
  const assignTo = getValues('assign_to');
  const orderType = getValues('order_type');
  const orderSet = getValues('order_set');
  const homeHealthAgencyName = getValues('home_health_agency_name');
  const externalHospiceAgencyName = getValues('external_hospice_agency_name');
  const externalHospiceReferralReason = getValues(
    'external_hospice_referral_reason',
  );
  const reasonForExternalReferral = getValues('reason_for_external_referral');
  const hospiceReferralOrigin = getValues('hospice_referral_origin');

  const isPharmacyTask = taskType === 'Pharmacy';
  const selectedRecurrence = getValues('recurrence');

  const orderTypeOptions = metadata?.order_type_options || [];
  const orderTypeCategory = metadata?.order_type_categories[orderType ?? ''];
  const orderSetOptions = metadata?.order_set_options[orderType ?? ''] ?? [];

  useEffect(() => {
    if (searchQuery && options.length > 0) {
      reset({
        ...initialValues,
        patient_id: options[0].id,
      });
    }
  }, [options, isOpen]);

  useEffect(() => {
    if (careTeamData && assignTo === 'assignedCm') {
      setValue('user_id', careTeamData.care_manager.id);
    }
  }, [patient_id, careTeamData]);

  useEffect(() => {
    if (isPharmacyTask) {
      setValue('title', t('worklist.addCustomTask.pharmacyTitle'), {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue('description', t('worklist.addCustomTask.pharmacyDescription'), {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue(
        'due_date',
        new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
        { shouldDirty: true, shouldValidate: true },
      );
    } else {
      setValue('title', '', { shouldDirty: true, shouldValidate: true });
      setValue('description', '', { shouldDirty: true, shouldValidate: true });
      setValue('due_date', '', { shouldDirty: true, shouldValidate: true });
    }
  }, [isPharmacyTask, setValue]);

  useEffect(() => {
    if (taskType === 'Care management') {
      setValue('assign_to', 'team', {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue('due_date', '', { shouldDirty: true, shouldValidate: true });
      setValue('recurrence', '-1', {
        shouldDirty: true,
        shouldValidate: true,
      });
    } else {
      setValue('recurrence', '-1', {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [taskType]);

  useEffect(() => {
    setValue('order_set', '');
  }, [orderType]);

  const allValues = useWatch({ control });
  // we use this in order to prevent an infinite loop in the useEffect below
  const prevValuesRef = useRef(allValues);

  useEffect(() => {
    if (!isEqual(prevValuesRef.current, allValues)) {
      prevValuesRef.current = allValues;
      trigger();
    }
  }, [allValues, trigger]);

  useEffect(() => {
    if (careTeamData?.care_manager.id) {
      setValue('assign_to', 'assignedCm', {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue('user_id', careTeamData?.care_manager.id, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setUserSearchQuery('');
    }
    if (
      !careTeamData?.care_manager.id &&
      allValues.assign_to === 'assignedCm'
    ) {
      setValue('assign_to', 'self', {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue('user_id', userProfile?.id, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [careTeamData?.care_manager.id]);

  return {
    formMethods,
    assignOptions,
    teamOptions,
    userOptions,
    recurrenceOptions,
    onSubmit,
    isSubmitting,
    isOpen,
    handleClose,
    handleOnClick,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleSaveChanges,
    handleDiscardChanges,
    isLoading: isPending,
    isValid,
    teamsSearchQuery,
    setTeamsSearchQuery,
    userSearchQuery,
    setUserSearchQuery,
    urgencyOptions: urgencyOptions.map(o => ({ id: o, name: o })),
    taskType,
    assignTo,
    orderType,
    orderSet,
    selectedRecurrence,
    orderTypeOptions: orderTypeOptions.map(o => ({ id: o, name: o })),
    orderTypeCategory,
    orderSetOptions: orderSetOptions.map(o => ({ id: o, name: o })),
    labRequirementsOptions: labRequirementsOptions.map(o => ({
      id: o,
      name: o,
    })),
    homeHealthAgencyNameOptions: homeHealthAgencyNameOptions.map(o => ({
      id: o,
      name: o,
    })),
    homeHealthProviderStatusOptions: homeHealthProviderStatusOptions.map(o => ({
      id: o,
      name: o,
    })),
    hospiceReferralOriginOptions: hospiceReferralOriginOptions.map(o => ({
      id: o,
      name: o,
    })),
    programOptions: programOptions.map(o => ({
      value: o.name,
      label: o.name,
    })),
    externalHospiceReferralReasonOptions:
      externalHospiceReferralReasonOptions.map(o => ({ id: o, name: o })),
    homeHealthAgencyName,
    externalHospiceAgencyName,
    externalHospiceReferralReason,
    hospiceReferralOrigin,
    reasonForExternalReferralOptions: reasonForExternalReferralOptions.map(
      o => ({
        value: o,
        label: o,
      }),
    ),
    reasonForExternalReferral,
    noteTypeOptions,
    careManagerId: careTeamData?.care_manager.id || '',
    userId: userProfile?.id || '',
    patientAdditionalInformation,
    disabled: !user?.permissions.includes('manage_patients'),
  };
};
